import React, { useState, useEffect, useCallback } from 'react';
import { ErrorReport } from '../types';
import { isAdmin, getErrorReports, updateErrorReport } from '../services/api';

// Admin-only Error Reports inbox (mirrors the AI Info admin pattern: App.tsx
// gates the toggle on isAdmin(), and this component early-returns for a non-admin
// as belt-and-suspenders; the backend enforces ADMIN on the mutating endpoint).
//
// - list / filter error reports by status
// - open one to see the symptom + AI diagnosis, edit status/assign/notes/fix-spec
// - "continue investigation in chat" -> opens an AIAssist conversation seeded
//   with the report + thread (admin tools are available server-side by role)
//
// The open-report BADGE lives in App.tsx (it polls getErrorReports there); this
// component owns the full inbox surface. All calls go through the axios `api`
// client (MSAL bearer + 401/403 interceptor).

const STATUS_FILTERS: { key: string; label: string }[] = [
  { key: 'open', label: 'Open' },
  { key: 'new', label: 'New' },
  { key: 'acknowledged', label: 'Acknowledged' },
  { key: 'in_progress', label: 'In progress' },
  { key: 'resolved', label: 'Resolved' },
  { key: 'dismissed', label: 'Dismissed' },
  { key: '', label: 'All' },
];

const STATUS_OPTIONS = ['new', 'acknowledged', 'in_progress', 'resolved', 'dismissed'];

const severityStyle = (sev?: string | null): string => {
  switch ((sev || '').toLowerCase()) {
    case 'high': return 'bg-red-100 text-red-800';
    case 'medium': return 'bg-amber-100 text-amber-800';
    case 'low': return 'bg-gray-100 text-gray-700';
    default: return 'bg-gray-100 text-gray-700';
  }
};

const statusStyle = (status?: string | null): string => {
  switch ((status || '').toLowerCase()) {
    case 'new': return 'bg-blue-100 text-blue-800';
    case 'acknowledged': return 'bg-indigo-100 text-indigo-800';
    case 'in_progress': return 'bg-amber-100 text-amber-800';
    case 'resolved': return 'bg-green-100 text-green-800';
    case 'dismissed': return 'bg-gray-100 text-gray-600';
    default: return 'bg-gray-100 text-gray-700';
  }
};

const fmt = (s?: string | null): string => {
  if (!s) return '';
  try { return new Date(s).toLocaleString(); } catch { return s; }
};

const ErrorReportsInbox: React.FC = () => {
  const [statusFilter, setStatusFilter] = useState<string>('open');
  const [reports, setReports] = useState<ErrorReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { reports } = await getErrorReports(statusFilter ? { status: statusFilter } : {});
      setReports(reports);
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to load error reports');
      setReports([]);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  // UX-only admin gate (App.tsx never renders us for a non-admin — defensive).
  if (!isAdmin()) return null;

  const selected = reports.find((r) => r.id === selectedId) || null;

  const onSaved = (updated: ErrorReport) => {
    setReports((prev) => prev.map((r) => (r.id === updated.id ? { ...r, ...updated } : r)));
  };

  return (
    <div className="flex-1 flex flex-col">
      {/* Sticky filter strip at the same top offset as the AI Info tab strip. */}
      <div className="bg-white px-3 sm:px-6 pt-3 pb-2 sticky top-16 z-30 border-b border-gray-300">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <h2 className="text-lg font-semibold text-gray-900">Error Reports</h2>
          <div className="flex items-center gap-1 flex-wrap">
            {STATUS_FILTERS.map((f) => (
              <button
                key={f.key || 'all'}
                onClick={() => { setStatusFilter(f.key); setSelectedId(null); }}
                className={
                  'px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ' +
                  (statusFilter === f.key
                    ? 'bg-brand-100 text-brand-700'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50')
                }
              >
                {f.label}
              </button>
            ))}
            <button
              onClick={load}
              title="Refresh"
              className="p-1.5 rounded-lg text-gray-400 hover:text-brand-700 hover:bg-gray-50"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 flex min-h-0">
        {/* List */}
        <div className="w-full md:w-96 flex-shrink-0 border-r border-gray-200 overflow-y-auto">
          {loading ? (
            <div className="p-4 text-sm text-gray-500">Loading…</div>
          ) : error ? (
            <div className="p-4 text-sm text-red-600">{error}</div>
          ) : reports.length === 0 ? (
            <div className="p-4 text-sm text-gray-500">No reports for this filter.</div>
          ) : (
            reports.map((r) => (
              <button
                key={r.id}
                onClick={() => setSelectedId(r.id)}
                className={
                  'block w-full text-left px-4 py-3 border-b border-gray-100 transition-colors ' +
                  (r.id === selectedId ? 'bg-brand-50' : 'hover:bg-gray-50')
                }
              >
                <div className="flex items-center gap-1.5 mb-1 flex-wrap">
                  <span className="text-xs text-gray-400">#{r.id}</span>
                  <span className={`px-1.5 py-0.5 text-[10px] font-semibold rounded-full ${severityStyle(r.severity)}`}>
                    {(r.severity || 'n/a').toUpperCase()}
                  </span>
                  <span className={`px-1.5 py-0.5 text-[10px] font-semibold rounded-full ${statusStyle(r.status)}`}>
                    {r.status}
                  </span>
                  {r.thread_ref != null && (
                    <span className="text-[10px] text-gray-400">Thread #{r.thread_ref}</span>
                  )}
                </div>
                <p className="text-sm font-medium text-gray-800 truncate">{r.title || '(untitled)'}</p>
                <p className="text-xs text-gray-500 truncate">
                  {r.category || 'other'} · {r.reported_by_name || r.reported_by_email || 'unknown'} · {fmt(r.created_at)}
                </p>
              </button>
            ))
          )}
        </div>

        {/* Detail */}
        <div className="flex-1 min-w-0 overflow-y-auto hidden md:block">
          {selected ? (
            <ReportDetail report={selected} onSaved={onSaved} onReload={load} />
          ) : (
            <div className="p-6 text-sm text-gray-400">Select a report to view details.</div>
          )}
        </div>
      </div>

      {/* On mobile the detail renders as a full-width overlay under the list. */}
      {selected && (
        <div className="md:hidden fixed inset-0 top-16 bg-white z-40 overflow-y-auto">
          <div className="p-2">
            <button
              onClick={() => setSelectedId(null)}
              className="text-sm text-brand-700 mb-2 flex items-center gap-1"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to list
            </button>
          </div>
          <ReportDetail report={selected} onSaved={onSaved} onReload={load} />
        </div>
      )}
    </div>
  );
};

// Report detail + admin triage editor. Status / assign / admin notes / fix-spec
// are editable (admin-only, backend-enforced). "Continue investigation in chat"
// launches an AIAssist conversation seeded with the report's thread + a starter
// prompt built from the report.
const ReportDetail: React.FC<{
  report: ErrorReport;
  onSaved: (updated: ErrorReport) => void;
  onReload: () => void;
}> = ({ report, onSaved, onReload }) => {
  const [status, setStatus] = useState(report.status);
  const [assign, setAssign] = useState(report.assigned_admin_email || '');
  const [notes, setNotes] = useState(report.admin_notes || '');
  const [fixSpec, setFixSpec] = useState(report.fix_spec || '');
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState<string | null>(null);
  const [saveErr, setSaveErr] = useState<string | null>(null);

  // Reset the editor when a different report is selected.
  useEffect(() => {
    setStatus(report.status);
    setAssign(report.assigned_admin_email || '');
    setNotes(report.admin_notes || '');
    setFixSpec(report.fix_spec || '');
    setSaveMsg(null);
    setSaveErr(null);
  }, [report.id, report.status, report.assigned_admin_email, report.admin_notes, report.fix_spec]);

  const save = async () => {
    setSaving(true);
    setSaveMsg(null);
    setSaveErr(null);
    try {
      const updated = await updateErrorReport(report.id, {
        status,
        assigned_admin_email: assign,
        admin_notes: notes,
        fix_spec: fixSpec,
      });
      onSaved(updated || { ...report, status, assigned_admin_email: assign, admin_notes: notes, fix_spec: fixSpec });
      setSaveMsg('Saved.');
      // A status change can move the row out of the current filter — reload.
      if (status !== report.status) onReload();
    } catch (err: any) {
      setSaveErr(err?.response?.data?.error || err?.message || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const continueInChat = () => {
    const seed =
      `Continue investigating error report #${report.id}: "${report.title}".` +
      (report.description ? `\n\nReported symptom: ${report.description}` : '') +
      (report.ai_diagnosis ? `\n\nPrior AI diagnosis: ${report.ai_diagnosis}` : '');
    // Launch AIAssist seeded with the report's thread + a starter prompt. App.tsx
    // listens for this window event (same launcher pattern as the feed's
    // 'sl-focus-convo'), opens the assist panel, and hands the payload to AIAssist.
    window.dispatchEvent(
      new CustomEvent('sl-open-assist', {
        detail: { threadRef: report.thread_ref ?? null, message: seed },
      })
    );
  };

  const field = 'w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-500';
  const label = 'block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1';

  return (
    <div className="p-4 sm:p-6 space-y-5">
      <div>
        <div className="flex items-center gap-1.5 mb-2 flex-wrap">
          <span className="text-sm text-gray-400">#{report.id}</span>
          <span className={`px-1.5 py-0.5 text-[10px] font-semibold rounded-full ${severityStyle(report.severity)}`}>
            {(report.severity || 'n/a').toUpperCase()}
          </span>
          <span className={`px-1.5 py-0.5 text-[10px] font-semibold rounded-full ${statusStyle(report.status)}`}>
            {report.status}
          </span>
          <span className="text-xs text-gray-500">{report.category || 'other'}</span>
          {report.thread_ref != null && (
            <span className="text-xs text-brand-700 font-medium">Thread #{report.thread_ref}</span>
          )}
        </div>
        <h3 className="text-lg font-semibold text-gray-900">{report.title || '(untitled)'}</h3>
        <p className="text-xs text-gray-500 mt-1">
          Reported by {report.reported_by_name || report.reported_by_email || 'unknown'}
          {report.reported_by_role ? ` (${report.reported_by_role})` : ''} · {fmt(report.created_at)}
        </p>
      </div>

      {report.description && (
        <div>
          <span className={label}>Symptom</span>
          <p className="text-sm text-gray-700 whitespace-pre-wrap">{report.description}</p>
        </div>
      )}

      {report.ai_diagnosis && (
        <div>
          <span className={label}>AI diagnosis</span>
          <p className="text-sm text-gray-700 whitespace-pre-wrap bg-gray-50 border border-gray-200 rounded-lg p-3">
            {report.ai_diagnosis}
          </p>
        </div>
      )}

      <div className="pt-2 border-t border-gray-200">
        <button
          onClick={continueInChat}
          className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-white bg-brand-700 rounded-lg hover:bg-brand-800"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.86 9.86 0 01-4-.8L3 20l1.3-3.9A7.96 7.96 0 013 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
          Continue investigation in chat
        </button>
      </div>

      {/* Admin triage editor */}
      <div className="pt-4 border-t border-gray-200 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={label}>Status</label>
            <select value={status} onChange={(e) => setStatus(e.target.value)} className={field}>
              {STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={label}>Assigned admin (email)</label>
            <input
              type="email"
              value={assign}
              onChange={(e) => setAssign(e.target.value)}
              placeholder="admin@jandjbagging.com"
              className={field}
            />
          </div>
        </div>

        <div>
          <label className={label}>Fix spec (root cause · affected files · repro)</label>
          <textarea
            value={fixSpec}
            onChange={(e) => setFixSpec(e.target.value)}
            rows={5}
            placeholder="Root cause, affected files, reproduction steps…"
            className={field + ' resize-y'}
          />
        </div>

        <div>
          <label className={label}>Admin notes</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            className={field + ' resize-y'}
          />
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={save}
            disabled={saving}
            className="px-4 py-2 text-sm font-medium text-white bg-brand-700 rounded-lg hover:bg-brand-800 disabled:opacity-40"
          >
            {saving ? 'Saving…' : 'Save changes'}
          </button>
          {saveMsg && <span className="text-sm text-green-700">{saveMsg}</span>}
          {saveErr && <span className="text-sm text-red-600">{saveErr}</span>}
        </div>
      </div>
    </div>
  );
};

export default ErrorReportsInbox;
